import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDeepCompareMemo } from "use-deep-compare"
import isEqual from 'react-fast-compare'

import { pageStore } from "state/store-zustand";

import { Marker } from "!react-map-gl"
import Pulse from './marker/pulse'
import Number from './marker/number'
import Icon from 'components/elements/icons/iconCategory'

import { getThumbnails } from 'components/elements/image/imageUtils'

import { formatDateTime } from 'vibemap-constants/dist/helpers.js'

const Markers = ({
  bounds,
  features = [],
  handleClick,
  handleOnMouseOver,
  handleOnMouseOut,
  iconSize = 30,
  markerSizeRem = 5,
  numMinMarkers = 10,
  numMaxMarkers = 40,
  numMaxMarkerMobile = 12,
  numColor = null,
  placeCurrent,
  placeFocused,
  shouldCluster,
  showNumbers = false,
  showClusterSize = false,
  showMarkerImage = true,
  showLabel = false,
  isMobile = false,
  debug = false,
  ...props
}) => {
  const { themeObject } = pageStore((state) => state);
  const photoMarkersCount = useRef(0)

  // TODO: consolidate with markerImage.js
  const featureChanged = (prevProps, nextProps) => {
    return isEqual(prevProps.feature, nextProps.feature);
  }

  const PhotoMarker = React.memo(
    ({
      feature,
      i,
      name,
      cluster_num,
      in_cluster,
      hasPlaceSelected,
      isCurrentMarker,
      isFocusedMarker,
      isEvent,
      latitude,
      longitude,
      showClusterSize,
      showImageByDefault = false,
      showNumbers,
      showPulse,
      start_date,
      top_category,
      top_in_cluster,
      thumbnail,
      vibes,
      ...props
    }) => {
      const minSize = 10
      const [hovering, setHovering] = useState(false)
      const [hasError, setHasError] = useState(false)
      const [imageSize, setImageSize] = useState(iconSize)

      const onError = useCallback((e) => {
        console.log('Error loading image ', e, name)
        setImageSize(minSize)
        setHasError(true)
      }, [name])

      const showImage = showImageByDefault || hovering
      feature.properties.showImage = showImage

      const hasCategoryIcon = top_category && top_category !== 'all' && top_category !== 'place'
      const categoryIcon = hasCategoryIcon ? <Icon category={top_category} onError={onError} /> : null

      useEffect(() => {
        const newSize = isCurrentMarker
          ? iconSize + 10 + (hovering ? 2 : 0) + 'px'
          : hovering
            ? iconSize + 5 + 'px'
            : hasCategoryIcon || showImage
              ? iconSize + 'px'
              : minSize + 'px'

        setImageSize(newSize)
      }, [isCurrentMarker, hovering, iconSize, hasCategoryIcon, showImage])

      const opacity = hasPlaceSelected ? (isCurrentMarker ? 1.0 : 0.7) : 1.0

      const image = showImage ? (
        <img
          draggable={false}
          src={thumbnail}
          style={{
            minHeight: imageSize,
            minWidth: imageSize,
            opacity: opacity,
          }}
        />
      ) : (
        categoryIcon
      )

      const labelText = isEvent ? formatDateTime(start_date, true, true) : vibes?.length > 0 ? vibes[0] : null

      const Label = <span className={`marker-label ${isEvent ? 'event-marker' : ''}`}>{labelText}</span>

      const debugClass = debug ? `${in_cluster ? 'clustered' : ''} ${cluster_num > 0 ? 'cluster_' + cluster_num : ''}` : ''
      const baseClass = `marker ${showImage ? 'photo' : 'icon'} ${isCurrentMarker ? 'selected' : 'unselected'}`
      const markerClass = debug ? `${baseClass} ${debugClass}` : baseClass

      const markerStyle = {
        borderWidth: isCurrentMarker ? '4px' : '1px',
        minHeight: `${markerSizeRem}rem`,
        minWidth: `${markerSizeRem}rem`,
        height: imageSize,
        width: imageSize,
        zIndex: isCurrentMarker ? 20 : showImage ? 15 : 2,
      }

      const clusterStyle = {
        fontSize: '0.6rem',
        zIndex: 10,
      }

      const IconNoImage = (
        <a>
          {categoryIcon}
          {debug && cluster_num > 0 ? <span className="cluster-number">{cluster_num}</span> : null}
        </a>
      )

      const onMouseOver = useCallback(
        (e) => {
          setHovering(true)
          handleOnMouseOver(e, feature)
        },
        [handleOnMouseOver, feature]
      )

      const onMouseOut = useCallback(
        (e) => {
          setHovering(false)
          handleOnMouseOut(e, feature)
        },
        [handleOnMouseOut, feature]
      )

      if (hasError) return null

      return (
        <Marker key={i} longitude={longitude} latitude={latitude} style={markerStyle}>
          {showLabel ? Label : null}
          {showPulse && <Pulse show={showPulse} />}
          {image ? (
            <a
              onClick={(e) => handleClick(e, feature)}
              onMouseOver={(e) => onMouseOver(e)}
              onMouseOut={(e) => onMouseOut(e)}
              className={markerClass}
            >
              {image}
            </a>
          ) : (
            IconNoImage
          )}
          {showClusterSize ? (
            <span className="cluster-size" style={clusterStyle}>
              + {feature.properties.cluster_size} more
            </span>
          ) : null}
        </Marker>
      )
    },
    featureChanged
  )

  const placeCurrentDep = JSON.stringify(placeCurrent)
  const placeFocusedDep = JSON.stringify(placeFocused)

  const featureMarkers = useDeepCompareMemo(() => {
    photoMarkersCount.current = 0

    return features
      .filter((feature) => feature && feature?.properties)
      .map((feature, i) => {
        const {
          name,
          cluster_num,
          geometry,
          in_cluster,
          top_category,
          top_in_cluster,
          start_date,
          vibes,
        } = feature.properties

        const selectedPlace = placeCurrent || placeFocused || null
        console.log('selectedPlace', selectedPlace, placeCurrent, placeFocused);
        const isCurrentMarker = selectedPlace && selectedPlace.id == feature.id
        const isFocusedMarker = (placeCurrent?.id ?? placeFocused?.id) === feature.id
        const isEvent = feature.cardState?.card_type == 'event'

        const lat = feature?.geometry ? feature.geometry.coordinates[1] : 0
        const lon = feature?.geometry ? feature.geometry.coordinates[0] : 0

        const thumbnail = getThumbnails(feature)
        const hasThumbnail = thumbnail !== null

        const shouldShowClusterSize = showClusterSize && in_cluster

        const ignore_cluster = !shouldCluster
        const isTopOrStandalone = isCurrentMarker || isFocusedMarker || top_in_cluster || !in_cluster || ignore_cluster
        const hasReachedMin = photoMarkersCount.current >= numMinMarkers
        const hasReachedMax = photoMarkersCount.current >= (isMobile ? numMaxMarkerMobile : numMaxMarkers)
        const showPulse = isCurrentMarker

        const showImage = showMarkerImage && hasThumbnail && isTopOrStandalone && showNumbers == false && !hasReachedMax

      const hasCategoryIcon = top_category && top_category !== 'all' && top_category !== 'place'
      const categoryIcon = hasCategoryIcon
        ? <Icon category={top_category} />
        : null

      // TODO: make this a hook w/ better theme logic

      const numStyle = {
        backgroundColor: themeObject?.numBgColor,
        color: themeObject?.numTextColor,
        border: `2px solid ${themeObject?.numBorderColor}`,
        zIndex: 110,
        //left: showMarkerImage ? '0.4rem' : '1.4rem',
        //top: showMarkerImage ? '0.4rem' : '1.4rem',
        padding: '1px',
        height: themeObject?.numBadgeSize,
        width: themeObject?.numBadgeSize,
      }

      // TODO: this can still be in the image marker
      // It's more like a rich marker with image, icon, number
      if (!showImage && !showNumbers) {
        return categoryIcon
      }

        if (showImage) {
          photoMarkersCount.current += 1
        }

        if (showNumbers && !showImage) {
          return (
            <Marker
              key={i}
              longitude={lon}
              latitude={lat}
              style={{
                zIndex: 10,
                height: themeObject?.numBadgeSize,
                width: themeObject?.numBadgeSize,
              }}
            >
              <Number num={i + 1} className={`number marker-number ${themeObject?.numTheme}`} themeStyle={numStyle} />
            </Marker>
          )
        }

        return (
          <PhotoMarker
            key={i}
            i={i}
            name={name}
            in_cluster={in_cluster}
            hasPlaceSelected={selectedPlace}
            isCurrentMarker={isCurrentMarker}
            cluster_num={cluster_num}
            isEvent={isEvent}
            latitude={lat}
            longitude={lon}
            top_category={top_category}
            top_in_cluster={top_in_cluster}
            thumbnail={thumbnail}
            showClusterSize={shouldShowClusterSize}
            showImageByDefault={showImage}
            showPulse={showPulse}
            start_date={start_date}
            vibes={vibes}
            feature={feature}
          />
        )
      })
  }, [features, placeCurrentDep, placeFocusedDep, showNumbers])

  return featureMarkers
}

export default Markers